"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "@/i18n/LocaleProvider";
import { useSiteNavigation } from "@/lib/useSiteNavigation";
import LanguageSwitcher from "./LanguageSwitcher";

// Only the homepage has a full-bleed dark hero directly behind the header -- everywhere else
// starts on the plain light page background, where a transparent white-text header would be
// unreadable. Scoped to "/" rather than made a generic per-page prop since no other page has
// (or is likely to get) this treatment.
const TRANSPARENT_ELIGIBLE_PATH = "/";

export default function Header() {
  const { t } = useTranslations();
  const { navLinks } = useSiteNavigation();
  const pathname = usePathname();
  const eligible = pathname === TRANSPARENT_ELIGIBLE_PATH;

  // Lazy initializer (reads window.scrollY once, synchronously, at mount) rather than an
  // effect-based read -- same pattern as AnnouncementBannerDisplay's dismissed check -- so
  // there's no flash of the wrong header state on first paint.
  const [scrolled, setScrolled] = useState(() => (typeof window !== "undefined" ? window.scrollY > 40 : false));
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!eligible) return;
    function onScroll() {
      setScrolled(window.scrollY > 40);
    }
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [eligible]);

  // Closes the mobile panel on every route change -- covers both a nav-link tap (the panel
  // would otherwise still be open, now stacked over the new page) and the browser back button.
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  const transparent = eligible && !scrolled && !mobileOpen;

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-300 ${
        transparent
          ? "bg-transparent text-white"
          : "border-b border-foreground/10 bg-background/90 text-foreground backdrop-blur"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-[1440px] items-center justify-between px-6 sm:px-8 md:grid md:grid-cols-[1fr_auto_1fr]">
        <Link href="/" className="font-display text-2xl tracking-tight">
          {t("header.brand")}
        </Link>
        <nav className="hidden items-center gap-8 text-sm font-medium md:flex">
          {navLinks.map((link) => (
            <Link key={link.href} href={link.href} className="hover:text-accent">
              {link.label}
            </Link>
          ))}
        </nav>
        <div className="flex items-center justify-end gap-2 sm:gap-4">
          <div className="hidden sm:block">
            <LanguageSwitcher />
          </div>
          {/* ?fresh=1: a deliberate "start a new booking" click -- BookingFlow clears any saved
              in-progress draft when it sees this, unlike a plain /book refresh or back/forward
              navigation, which still resumes one. */}
          <Link
            href="/book?fresh=1"
            className="hidden rounded-xl bg-accent px-5 py-2 text-sm font-semibold text-accent-foreground transition hover:opacity-90 sm:inline-flex"
          >
            {t("header.bookSession")}
          </Link>
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? t("header.closeMenu") : t("header.openMenu")}
            aria-expanded={mobileOpen}
            className={`flex size-10 items-center justify-center rounded-full transition md:hidden ${
              transparent ? "text-white hover:bg-white/10" : "text-foreground hover:bg-foreground/5"
            }`}
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden>
              {mobileOpen ? <path d="M6 6l12 12M18 6 6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeInOut" }}
            className="overflow-hidden border-t border-foreground/10 bg-background text-foreground md:hidden"
          >
            <nav className="flex flex-col px-6 py-2 text-base font-medium">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="border-b border-foreground/5 py-3.5 last:border-b-0 hover:text-accent"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <div className="flex items-center justify-between gap-4 px-6 pb-5 pt-1">
              <LanguageSwitcher />
              <Link
                href="/book?fresh=1"
                className="rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition hover:opacity-90"
              >
                {t("header.bookSession")}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
